import { formatCurrency } from '@client/lib';
import { formatPercent, IRR_SUSPECT_MESSAGE } from '@shared';

import type { NormalizedPolicy, PremiumOption } from '@/features/policies-comparison';

export type IrrRank = 1 | 2 | null;

export interface PremiumBreakdown {
  hasMonthlyCost: boolean;
  hasLumpSum: boolean;
  hasLimitedPay: boolean;
  /** True when the policy's recurring premium is billed annually. */
  isAnnualBilled: boolean;
  annualPremiumFormatted?: string;
  limitedPayYears?: number;
  monthlyPremiumFormatted?: string;
  lumpSumFormatted?: string;
  irr?: number;
  irrFormatted?: string;
  irrRank?: IrrRank;
  isIrrSuspect?: boolean;
}

function buildBreakdown(option: PremiumOption): PremiumBreakdown {
  const hasMonthlyCost = (option.premiumMonthlyCost ?? 0) > 0;
  const hasLumpSum = (option.lumpSumPayment ?? 0) > 0;
  const hasLimitedPay = (option.limitedPayYears ?? 0) > 0;
  const isAnnualBilled = option.premiumFrequency === 'Annually';

  const irr = option.isIrrSuspect ? undefined : option.irr;
  const irrFormatted = option.isIrrSuspect
    ? IRR_SUSPECT_MESSAGE
    : option.irr != null
      ? formatPercent(option.irr, 2)
      : undefined;

  return {
    hasMonthlyCost,
    hasLumpSum,
    hasLimitedPay,
    isAnnualBilled,
    annualPremiumFormatted: hasMonthlyCost
      ? formatCurrency((option.premiumMonthlyCost ?? 0) * 12)
      : undefined,
    limitedPayYears: option.limitedPayYears ?? undefined,
    monthlyPremiumFormatted: hasMonthlyCost ? formatCurrency(option.premiumMonthlyCost) : undefined,
    lumpSumFormatted: hasLumpSum ? formatCurrency(option.lumpSumPayment) : undefined,
    irr,
    irrFormatted,
    isIrrSuspect: option.isIrrSuspect,
  };
}

export function getPremiumBreakdowns(policy: NormalizedPolicy): PremiumBreakdown[] {
  const options = policy.premiumOptions ?? [
    {
      premiumMonthlyCost: policy.premiumMonthlyCost,
      lumpSumPayment: policy.lumpSumPayment,
      limitedPayYears: policy.limitedPayYears,
      irr: policy.irr,
      isIrrSuspect: policy.isIrrSuspect,
      premiumFrequency: policy.premiumFrequency,
    },
  ];

  return options.map(buildBreakdown);
}

export function rankIrrAcrossPolicies(allBreakdowns: PremiumBreakdown[][]): void {
  const allWithIrr: { breakdown: PremiumBreakdown; irr: number }[] = [];

  for (const breakdowns of allBreakdowns) {
    for (const b of breakdowns) {
      if (b.irr != null) {
        allWithIrr.push({ breakdown: b, irr: b.irr });
      }
    }
  }

  if (allWithIrr.length < 2) return;

  allWithIrr.sort((a, b) => b.irr - a.irr);

  const bestIrr = allWithIrr[0].irr;
  const secondBestIrr = allWithIrr.find(x => x.irr < bestIrr)?.irr;

  for (const entry of allWithIrr) {
    if (entry.irr === bestIrr) {
      entry.breakdown.irrRank = 1;
    } else if (secondBestIrr != null && entry.irr === secondBestIrr) {
      entry.breakdown.irrRank = 2;
    }
  }
}

function formatBreakdown(b: PremiumBreakdown): string {
  const parts: string[] = [];

  if (b.hasMonthlyCost && b.hasLimitedPay) {
    parts.push(
      `${b.annualPremiumFormatted} - Annual Premium for ${b.limitedPayYears} years. Then no further premiums required.`,
    );
  } else if (b.hasMonthlyCost) {
    // Annually-billed policies show the actual yearly bill (e.g. $797/yr) so
    // the displayed unit matches the carrier's invoice. Monthly-billed
    // policies (and policies with unknown frequency) show $X/mo as before.
    parts.push(
      b.isAnnualBilled ? `${b.annualPremiumFormatted}/yr` : `${b.monthlyPremiumFormatted}/mo`,
    );
  }

  if (b.hasLumpSum) {
    parts.push(`${b.lumpSumFormatted} Single`);
  }

  return parts.join('\n');
}

export function formatPremium(policy: NormalizedPolicy): string {
  const breakdowns = getPremiumBreakdowns(policy);
  const lines = breakdowns.map(formatBreakdown).filter(Boolean);
  return lines.length > 0 ? lines.join('\n— or —\n') : '-';
}
